"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { SEMANTICS_KEY, THEME_KEY } from "./no-flash";
import { installFavicon } from "./favicon";

export type Theme = "light" | "dark";
export type Semantics = "rose-pine" | "catppuccin";

type ThemeContextValue = {
  /** Light/dark mode (drives the `.dark` class on <html>). */
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  /** Semantic palette (drives `data-semantics` on <html>). */
  semantics: Semantics;
  setSemantics: (s: Semantics) => void;
  toggleSemantics: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * RR7-friendly provider. The no-flash script (`themeInitScript`) sets `.dark` / `data-semantics`
 * on <html> before paint; this provider mirrors that into React state, persists changes to
 * localStorage, and keeps the DOM in sync. No `next-themes` dependency.
 */
export function ThemeProvider({
  children,
  defaultTheme = "light",
  defaultSemantics = "rose-pine",
  favicon = true,
}: {
  children: ReactNode;
  defaultTheme?: Theme;
  defaultSemantics?: Semantics;
  /** Install the Certu mark as the document favicon, if the page doesn't already have one. Every
   *  consumer gets it by default with zero extra wiring; set false to opt out. */
  favicon?: boolean;
}) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [semantics, setSemanticsState] = useState<Semantics>(defaultSemantics);

  // hydrate from the DOM state the no-flash script already applied (avoids a flash + double-set)
  useEffect(() => {
    const el = document.documentElement;
    setThemeState(el.classList.contains("dark") ? "dark" : "light");
    setSemanticsState(el.getAttribute("data-semantics") === "catppuccin" ? "catppuccin" : "rose-pine");
  }, []);

  useEffect(() => {
    if (!favicon) return;
    return installFavicon();
  }, [favicon]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  useEffect(() => {
    const el = document.documentElement;
    if (semantics === "catppuccin") el.setAttribute("data-semantics", "catppuccin");
    else el.removeAttribute("data-semantics");
    try { localStorage.setItem(SEMANTICS_KEY, semantics); } catch { /* ignore */ }
  }, [semantics]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);
  const setSemantics = useCallback((s: Semantics) => setSemanticsState(s), []);
  const toggleSemantics = useCallback(() => setSemanticsState((s) => (s === "catppuccin" ? "rose-pine" : "catppuccin")), []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, semantics, setSemantics, toggleSemantics }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a <ThemeProvider>");
  return ctx;
}
